import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Empty, Spin, Tag, Typography, Button } from 'antd';
import { EnvironmentOutlined, CloseOutlined, CarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

import GoogleMap from './Map/GoogleMap';
import { setShouldFitbounds } from '../redux/slice';
import { getSitePassages } from '../modules/sites/api';
import './SitesMapView.scss';

// OVO-169 — map of a partner's Sites; click a pin → detail + passage history.
// General feature (any partner / single-tenant). Reuses the GoogleMap wrapper.

const SitePin = () => (
  <div className="site-pin">
    <svg width="22" height="28" viewBox="0 0 22 28" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11 0C5 0 .5 4.5 .5 10.5.5 18 11 28 11 28S21.5 18 21.5 10.5C21.5 4.5 17 0 11 0Z"
        fill="var(--accent-strong)"
      />
      <circle cx="11" cy="10.5" r="4" fill="#fff" />
    </svg>
  </div>
);

// OVO-168 — several Sites can share one physical address (e.g. CT puts 2-4
// collection boxes at the same ecopoint → identical lat/lng). Without grouping
// their pins stack and only the top one is clickable. Mirrors the day-plan
// co-located merge (RouteMap.jsx): one badge per location showing the count.
const SiteClusterPin = ({ count }) => (
  <div className="site-cluster-pin">
    <svg width="30" height="38" viewBox="0 0 30 38" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15 0C7 0 .5 6 .5 14 .5 24 15 38 15 38S29.5 24 29.5 14C29.5 6 23 0 15 0Z"
        fill="var(--accent-strong)"
        stroke="#fff"
        strokeWidth="1.5"
      />
    </svg>
    <span className="scp-count">{count}</span>
  </div>
);

// Same "exact same spot" definition the day-plan uses (RouteMap `vehicleLocKey`).
const coordKey = (lat, lng) => `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;

const statusColor = (s) => {
  const v = String(s || '').toUpperCase();
  if (v === 'COMPLETED' || v === 'COLLECTED') return 'green';
  if (v.includes('FAILED') || v === 'ERROR') return 'red';
  if (v === 'SKIPPED') return 'orange';
  return 'default';
};

// `getPassages` lets the partner-viewer mount swap in the /partners/me/...
// scoped endpoint (OVO-002) — defaults to the admin one.
export default function SitesMapView({ sites = [], getPassages = getSitePassages }) {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState(null);
  // The co-located sites behind the clicked pin (length 1 for a lone site).
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [passages, setPassages] = useState(null);
  const [loading, setLoading] = useState(false);

  const withCoords = useMemo(() => sites.filter((s) => s.lat != null && s.lng != null), [sites]);

  // OVO-168 — collapse sites at the exact same coordinate into one pin.
  const groups = useMemo(() => {
    const byCoord = new Map();
    withCoords.forEach((s) => {
      const key = coordKey(s.lat, s.lng);
      if (!byCoord.has(key)) byCoord.set(key, []);
      byCoord.get(key).push(s);
    });
    return Array.from(byCoord.values());
  }, [withCoords]);

  // Fit the map to all pins whenever the (filtered) site set changes.
  useEffect(() => {
    dispatch(setShouldFitbounds(true));
  }, [groups.length, dispatch]);

  const markers = useMemo(
    () =>
      groups.map((group) => {
        const first = group[0];
        const isCluster = group.length > 1;
        return {
          // A merged pin stands for several site ids; `activeIds` lets GoogleMap
          // light it up when any of its sites is the selected one (same trick as
          // the day-plan merged-stop badge).
          id: isCluster ? `sites-${coordKey(first.lat, first.lng)}` : `site-${first.id}`,
          activeIds: group.map((s) => `site-${s.id}`),
          latitude: first.lat,
          longitude: first.lng,
          content: isCluster ? <SiteClusterPin count={group.length} /> : <SitePin />,
          onClick: () => {
            setSelectedGroup(group);
            setSelected(group[0]);
          },
        };
      }),
    [groups],
  );

  useEffect(() => {
    if (!selected?.id) {
      setPassages(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setPassages(null);
    getPassages(selected.id)
      .then(({ response }) => {
        if (!cancelled) setPassages(response || null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected?.id, getPassages]);

  if (withCoords.length === 0) {
    return (
      <Empty
        style={{ padding: 48 }}
        description="No sites with coordinates to map yet — add lat/lng on a site, or Discover from geocoded orders."
      />
    );
  }

  const missing = sites.length - withCoords.length;

  return (
    <div className="sites-map-view">
      <div className="smv-map">
        <GoogleMap markers={markers} active={selected ? { id: `site-${selected.id}` } : null} disableInfoWindow />
        <div className="smv-count">
          {withCoords.length} site{withCoords.length === 1 ? '' : 's'}
          {groups.length !== withCoords.length ? ` · ${groups.length} location${groups.length === 1 ? '' : 's'}` : ''}
          {missing > 0 ? ` · ${missing} without coordinates` : ''}
        </div>
      </div>

      {selected && (
        <div className="smv-panel">
          <div className="smv-panel-head">
            <Typography.Text strong>{selected.name}</Typography.Text>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => {
                setSelected(null);
                setSelectedGroup(null);
              }}
            />
          </div>
          {selectedGroup && selectedGroup.length > 1 && (
            <div className="smv-cluster">
              <span className="smv-cluster-label">{selectedGroup.length} sites at this location</span>
              <div className="smv-cluster-list">
                {selectedGroup.map((s) => (
                  <Tag.CheckableTag key={s.id} checked={selected?.id === s.id} onChange={() => setSelected(s)}>
                    {s.name}
                  </Tag.CheckableTag>
                ))}
              </div>
            </div>
          )}
          <div className="smv-addr">
            <EnvironmentOutlined /> {selected.address}
          </div>
          {selected.phone && <div className="smv-meta">{selected.phone}</div>}
          {selected.notes && <div className="smv-notes">{selected.notes}</div>}

          {loading ? (
            <Spin style={{ display: 'block', margin: '32px auto' }} />
          ) : passages ? (
            <>
              <div className="smv-summary">
                <span>
                  <b>{passages.summary.totalPassages}</b> passage{passages.summary.totalPassages === 1 ? '' : 's'}
                </span>
                {passages.summary.lastVisit && (
                  <span>last {dayjs(passages.summary.lastVisit).format('DD MMM YYYY')}</span>
                )}
                {passages.summary.totalWeight ? (
                  <span>
                    <b>{passages.summary.totalWeight.toFixed(1)}</b> kg total
                  </span>
                ) : null}
              </div>

              <div className="smv-passages">
                {passages.passages.length === 0 && (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No passages yet" />
                )}
                {passages.passages.map((p) => (
                  <div className="smv-passage" key={`${p.orderId}-${p.side}`}>
                    <div className="smv-passage-top">
                      <span className="smv-date">{dayjs(p.date).format('DD MMM YYYY')}</span>
                      <Tag color={p.side === 'pickup' ? 'blue' : 'geekblue'}>{p.side}</Tag>
                      <Tag color={statusColor(p.status)}>{p.status}</Tag>
                    </div>
                    <div className="smv-passage-bot">
                      {p.driverName && (
                        <span>
                          <CarOutlined /> {p.driverName}
                        </span>
                      )}
                      {p.weight ? <span>{p.weight.toFixed(1)} kg</span> : null}
                      {p.routeId && <Link to={`/admin/routes/${p.routeId}`}>route #{p.routeId}</Link>}
                      <Link to={`/admin/orders/${p.orderCode}`}>{p.orderCode}</Link>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
